import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Home, Package, FolderTree, Tag, FileText, Lock, Loader2, Search } from "lucide-react";
import SeoEditor from "./SeoEditor";

const TYPES = [
  ["homepage", "Homepage", Home],
  ["product", "Products", Package],
  ["category", "Categories", FolderTree],
  ["brand", "Brands", Tag],
  ["service", "Service pages", FileText],
];

export default function EntityEditors() {
  const [etype, setEtype] = useState("homepage");
  const [ekey, setEkey] = useState("home");
  const [targets, setTargets] = useState(null);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");

  const loadTargets = useCallback(() => {
    if (etype === "homepage") { setTargets([{ key: "home", label: "Homepage" }]); return; }
    setTargets(null);
    api.get(`/admin/seo/entity/${etype}/targets`, { params: { q, limit: 50 } })
      .then((r) => setTargets(r.data.items)).catch(() => setTargets([]));
  }, [etype, q]);
  useEffect(loadTargets, [loadTargets]);

  const pickType = (t) => {
    setEtype(t); setQ(""); setQInput("");
    if (t === "homepage") setEkey("home"); else setEkey(null);
  };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6" data-testid="entity-editors">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {TYPES.map(([id, label, Icon]) => (
            <button key={id} onClick={() => pickType(id)} data-testid={`etype-${id}`}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold border ${etype === id ? "bg-steel text-white border-steel" : "bg-white text-steel hover:border-accent"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {etype !== "homepage" && (
          <div className="rounded-xl border bg-white">
            <div className="p-2 border-b flex gap-1">
              <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setQ(qInput)} placeholder={`Search ${etype}…`} className="h-8" data-testid="target-search-input" />
              <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setQ(qInput)}><Search size={14} /></Button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y">
              {targets === null ? <p className="p-3 text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Loading…</p>
                : targets.length === 0 ? <p className="p-3 text-xs text-muted-foreground">No results.</p>
                : targets.map((t) => (
                  <button key={t.key} onClick={() => setEkey(t.key)} data-testid={`target-${t.key}`}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-slate-50 ${ekey === t.key ? "bg-amber-50" : ""}`}>
                    <span className="line-clamp-1 text-steel">{t.label}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      {t.custom && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Has custom SEO" />}
                      {t.locked && <Lock size={12} className="text-amber-600" />}
                    </span>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
      <div className="rounded-xl border bg-white p-5">
        {ekey ? <SeoEditor etype={etype} ekey={ekey} onSaved={loadTargets} />
          : <div className="h-full grid place-items-center text-sm text-muted-foreground py-20">Select a {etype} from the list to edit its SEO.</div>}
      </div>
    </div>
  );
}
